import classNames from "classnames";
import React, { ReactNode, useState } from "react";
import "./ClassicyWindow.scss";

interface SimpleClassicyWindowProps {
  title?: string;
  icon?: string;
  closable?: boolean;
  zoomable?: boolean;
  collapsable?: boolean;
  scrollable?: boolean;
  modal?: boolean;
  isActive?: boolean;
  width?: number | string;
  height?: number | string;
  top?: number | string;
  left?: number | string;
  onClose?: () => void;
  children?: ReactNode;
  className?: string;
}

export const SimpleClassicyWindow: React.FC<SimpleClassicyWindowProps> = ({
  title = "",
  icon,
  closable = true,
  zoomable = false,
  collapsable = false,
  scrollable = true,
  modal = false,
  isActive = true,
  width = 800,
  height = 600,
  top,
  left,
  onClose,
  children,
  className,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [zoomed, setZoomed] = useState(false);

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const toggleCollapse = () => {
    if (collapsable) {
      setCollapsed(!collapsed);
      if (zoomed) {
        setZoomed(false);
      }
    }
  };

  const toggleZoom = () => {
    if (zoomable) {
      setZoomed(!zoomed);
      if (collapsed) {
        setCollapsed(false);
      }
    }
  };

  const renderTitleBar = () => {
    if (title !== "") {
      return (
        <>
          <div className="classicyWindowTitleLeft"></div>
          {icon && (
            <div className="classicyWindowIcon">
              <img src={icon} alt={title} />
            </div>
          )}
          <div className="classicyWindowTitleText">
            <p>{title}</p>
          </div>
          <div className="classicyWindowTitleRight"></div>
        </>
      );
    }
    return <div className="classicyWindowTitleCenter"></div>;
  };

  return (
    <div
      className={classNames(
        "classicyWindow",
        collapsed && "classicyWindowCollapsed",
        zoomed && "classicyWindowZoomed",
        isActive ? "classicyWindowActive" : "classicyWindowInactive",
        modal && "classicyWindowModal",
        !scrollable && "classicyWindowNoScroll",
        className
      )}
      style={{
        position: 'absolute',
        width: typeof width === 'number' ? `${width}px` : width,
        height: collapsed ? 'auto' : (typeof height === 'number' ? `${height}px` : height),
        top: top !== undefined ? (typeof top === 'number' ? `${top}px` : top) : undefined,
        left: left !== undefined ? (typeof left === 'number' ? `${left}px` : left) : undefined,
      }}
    >
      <div
        className={classNames(
          "classicyWindowTitleBar",
          modal && "classicyWindowTitleBarModal"
        )}
      >
        {closable && (
          <div className="classicyWindowControlBox">
            <div className="classicyWindowCloseBox" onClick={handleClose}></div>
          </div>
        )}
        <div className="classicyWindowTitle">{renderTitleBar()}</div>
        {zoomable && (
          <div className="classicyWindowControlBox">
            <div className="classicyWindowZoomBox" onClick={toggleZoom}></div>
          </div>
        )}
        {collapsable && (
          <div className="classicyWindowControlBox">
            <div
              className="classicyWindowCollapseBox"
              onClick={toggleCollapse}
            ></div>
          </div>
        )}
      </div>

      <div
        className={classNames(
          modal ? "classicyWindowContentsModal" : "classicyWindowContents",
          !isActive && "classicyWindowContentsDimmed",
          !scrollable && "classicyWindowNoScroll"
        )}
        style={{
          display: collapsed ? "none" : "block",
        }}
      >
        <div
          className={classNames(
            "classicyWindowContentsInner",
            modal && "classicyWindowContentsModalInner"
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default SimpleClassicyWindow;